import { Skeleton } from "../../shadcn-components/ui/skeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../shadcn-components/ui/card";
import { Link } from "react-router-dom";
import { BiSpreadsheet } from "react-icons/bi";
import { HiOutlineChevronRight, HiOutlineDocumentText } from "react-icons/hi";
import { useEffect, useState } from "react";
import dashboardDocumentCount from "../../api/dashboardDocumentCount";
import { useRoleContext } from "../../context/RolesContext";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import EmailIcon from "@mui/icons-material/Email";
import FaxIcon from "@mui/icons-material/Fax";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import InboxIcon from "@mui/icons-material/Inbox";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PendingRequestsCard from "./PendingRequestsCard";

function DocCountCard({ token, customBoxShadow, is_admin, userId }) {
  const savedRoleId = localStorage.getItem("role_id");

  // Role IDs
  // const BACKOFFICE_TEAM_LEADER_ROLE_ID = "3bfdf38f-5084-4989-b2a8-766373cfc079";
  // const USER_ROLE_ID = "f083d7ba-4884-47bc-8514-9606f6363c12";

  const BACKOFFICE_TEAM_LEADER_ROLE_ID = "b5388e4d-a259-42cb-b071-23d1934d2c11";
  const USER_ROLE_ID = "2257087c-2dea-418f-9a8a-77b7f173f0c3";

  // State & permissions
  const [countLoading, setCountLoading] = useState(true);
  const [documentCount, setDocumentCount] = useState({});
  const { permissions } = useRoleContext();

  // Fetch counts from API
  const documentCountData = async () => {
    const response = await dashboardDocumentCount({
      token,
      savedRoleId,
      is_admin,
    });
    if (response.code === 200) {
      setDocumentCount(response.data);
    }
  };

  useEffect(() => {
    async function fetchData() {
      await documentCountData();
      setCountLoading(false);
    }
    fetchData();
  }, [token]);

  // All cards with counts and permissions
  const docCards = [
    {
      linkTo: "/unassigned-case-list",
      cardTitle: "All Unassigned",
      icon: <InboxIcon sx={{ fontSize: 18 }} />,
      docCount: documentCount.allUnassigned,
      permission: "view_all_unassigned",
    },
    {
      linkTo: "/assigned-case-list",
      cardTitle: "All Assigned",
      icon: <AssignmentTurnedInIcon sx={{ fontSize: 18 }} />,
      docCount: documentCount.allAssigned,
      permission: "view_all_assigned",
    },
    {
      linkTo: "/my-assigned-case-list",
      cardTitle: "My Assigned",
      icon: <AssignmentIndIcon sx={{ fontSize: 18 }} />,
      docCount: documentCount.myAssigned,
      permission: "view_my_assigned",
    },
    {
      linkTo: "/processed-documents",
      cardTitle: "Processed Today",
      icon: <AiOutlineCheckCircle />,
      docCount: documentCount.processedCount,
      permission: "view_all_documents",
    },
    {
      linkTo: "/unprocessed-documents",
      cardTitle: "Documents Pending (Unprocessed Documents)",
      icon: <PendingActionsIcon sx={{ fontSize: 18 }} />,
      docCount: documentCount.pendingCount,
      permission: "view_all_documents",
    },
    {
      linkTo: "/email-case-list",
      cardTitle: "Incoming Emails",
      icon: <EmailIcon sx={{ fontSize: 18 }} />,
      docCount: documentCount.emailCount,
      permission: "view_incoming_emails",
    },
    {
      linkTo: "/fax-case-list",
      cardTitle: "Incoming Faxes",
      icon: <FaxIcon sx={{ fontSize: 18 }} />,
      docCount: documentCount.faxCount,
      permission: "view_incoming_faxes",
    },
    {
      linkTo: "/scan-case-list",
      cardTitle: "Scanned Files",
      icon: <DocumentScannerIcon sx={{ fontSize: 18 }} />,
      docCount: documentCount.scannedCount,
      permission: "view_scanned_files",
    },
    {
      linkTo: "/clients-documents-reports",
      cardTitle: "Documents Reports of Clients",
      icon: <BiSpreadsheet />,
      permission: "view_clients_documents_reports",
    },
    {
      linkTo: "/users-documents-reports",
      cardTitle: "Documents Reports of Users",
      icon: <BiSpreadsheet />,
      permission: "view_users_documents_reports",
    },
  ];

  // Allowed cards per role
  const backofficeTeamLeaderCards = [
    "All Unassigned",
    "All Assigned",
    "Documents Pending (Unprocessed Documents)",
    "Documents Reports of Clients",
  ];

  const userRoleCards = [
    "My Assigned",
    "Documents Pending (Unprocessed Documents)",
    "Processed Today",
  ];

  // After defining docCards array, update the specific card conditionally:

  const updatedDocCardsWithUserId = docCards.map((card) => {
    if (card.cardTitle === "Documents Pending (Unprocessed Documents)") {
      if (savedRoleId !== BACKOFFICE_TEAM_LEADER_ROLE_ID && is_admin !== "1") {
        return {
          ...card,
          linkTo: `/unprocessed-documents?assignUser=${userId}`,
        };
      }
    }
    if (card.cardTitle === "Processed Today") {
      if (savedRoleId !== BACKOFFICE_TEAM_LEADER_ROLE_ID && is_admin !== "1") {
        return {
          ...card,
          linkTo: `/processed-documents?assignUser=${userId}`,
        };
      }
    }
    return card;
  });

  // Then use updatedDocCardsWithUserId in place of docCards for filtering & mapping

  // Filter cards by role using updated list
  let filteredDocCards = updatedDocCardsWithUserId;
  if (savedRoleId === BACKOFFICE_TEAM_LEADER_ROLE_ID) {
    filteredDocCards = updatedDocCardsWithUserId.filter((card) =>
      backofficeTeamLeaderCards.includes(card.cardTitle)
    );
  } else if (savedRoleId === USER_ROLE_ID) {
    filteredDocCards = updatedDocCardsWithUserId.filter((card) =>
      userRoleCards.includes(card.cardTitle)
    );
  } else {
    filteredDocCards = updatedDocCardsWithUserId.filter(
      (card) => card.cardTitle !== "Processed Today"
    );
  }

  // Permission check helper
  const hasPermission = (permission) => {
    if (is_admin === "1") {
      return true;
    }
    return permissions.includes(permission);
  };

  // Apply permission filtering
  const updatedDocCards = filteredDocCards.map((card) => ({
    ...card,
    linkTo: hasPermission(card.permission) ? card.linkTo : null,
  }));

  // Split the (already role- and permission-filtered) cards into the three
  // visual groups used by the new compact layout — which cards land in which
  // group is purely presentational; role/permission gating above is untouched.
  const SOURCE_TITLES = ["Incoming Emails", "Incoming Faxes", "Scanned Files"];
  const REPORT_TITLES = [
    "Documents Reports of Clients",
    "Documents Reports of Users",
  ];

  const topCards = updatedDocCards.filter(
    (card) =>
      !SOURCE_TITLES.includes(card.cardTitle) &&
      !REPORT_TITLES.includes(card.cardTitle)
  );
  const sourceCards = updatedDocCards.filter((card) =>
    SOURCE_TITLES.includes(card.cardTitle)
  );
  const reportCards = updatedDocCards.filter((card) =>
    REPORT_TITLES.includes(card.cardTitle)
  );

  const TOP_CARD_ACCENT_MAP = {
    "All Unassigned": { bg: "bg-slate-100", text: "text-slate-500" },
    "All Assigned": { bg: "bg-indigo-50", text: "text-indigo-600" },
    "My Assigned": { bg: "bg-teal-50", text: "text-teal-600" },
    "Documents Pending (Unprocessed Documents)": {
      bg: "bg-amber-50",
      text: "text-amber-600",
    },
    "Processed Today": { bg: "bg-emerald-50", text: "text-emerald-600" },
  };
  const DEFAULT_TOP_CARD_ACCENT = { bg: "bg-slate-100", text: "text-slate-500" };
  const getTopCardAccent = (cardTitle) =>
    TOP_CARD_ACCENT_MAP[cardTitle] || DEFAULT_TOP_CARD_ACCENT;
  // Keyed by card title so the donut segment and the legend badge for a
  // given source always render in the same color.
  const SOURCE_COLOR_MAP = {
    "Incoming Emails": "#14b8a6", // teal
    "Scanned Files": "#22c55e", // green
    "Incoming Faxes": "#a855f7", // purple
  };
  const getSourceColor = (cardTitle) =>
    SOURCE_COLOR_MAP[cardTitle] || "#94a3b8";
  const SOURCE_DESCRIPTION_MAP = {
    "Incoming Emails": "Delivered to inbox",
    "Incoming Faxes": "Received via fax",
    "Scanned Files": "Uploaded / scanned",
  };
  const getSourceDescription = (cardTitle) =>
    SOURCE_DESCRIPTION_MAP[cardTitle] || "";

  const isUserRole = savedRoleId === USER_ROLE_ID;
  const sourceTotal = sourceCards.reduce(
    (sum, card) => sum + (Number(card.docCount) || 0),
    0
  );
  const pieData = sourceCards.map((card) => ({
    name: card.cardTitle,
    value: Number(card.docCount) || 0,
  }));

  const renderTopCard = (card) => {
    const { linkTo, cardTitle, icon, docCount } = card;
    const accent = getTopCardAccent(cardTitle);
    const isMyAssigned = cardTitle === "My Assigned";
    const isPending = cardTitle === "Documents Pending (Unprocessed Documents)";
    const displayTitle = isPending ? "Documents Pending" : cardTitle;

    if (isUserRole && isMyAssigned) {
      return (
        <Card key={cardTitle} className="w-full p-4" style={customBoxShadow}>
          {linkTo ? (
            <>
              <div className="flex items-start justify-between gap-2">
                <Link to={linkTo} className="text-sm font-medium text-gray-600">
                  My Assigned
                </Link>
                <div className={`p-2 rounded-lg ${accent.bg} ${accent.text}`}>
                  {icon}
                </div>
              </div>
              <Link to={linkTo}>
                <div className="text-2xl font-semibold mt-2">
                  {countLoading ? (
                    <Skeleton className="h-8 w-1/2" />
                  ) : (
                    docCount
                  )}
                </div>
              </Link>
              <Link to="/documents" className="text-xs text-gray-500 hover:underline">
                Total Assigned -{" "}
                {countLoading ? (
                  <Skeleton className="h-4 w-8 inline-block align-middle" />
                ) : (
                  documentCount.myAllAssigned
                )}
              </Link>
            </>
          ) : (
            <Skeleton className="h-16 w-full" />
          )}
        </Card>
      );
    }

    const cardBody = (
      <Card
        className={`w-full p-4 h-full ${
          linkTo ? "hover:bg-teal-50 cursor-pointer" : "bg-gray-100 cursor-not-allowed"
        } ${isPending ? "border-l-4 border-l-amber-400" : ""}`}
        style={customBoxShadow}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-medium text-gray-600">
            {displayTitle}
          </span>
          <div className={`p-2 rounded-lg ${accent.bg} ${accent.text}`}>
            {icon}
          </div>
        </div>
        <div className="text-2xl font-semibold mt-2">
          {countLoading ? <Skeleton className="h-8 w-1/2" /> : docCount}
        </div>
        {isPending && (
          <div className="text-xs text-gray-400 mt-1">Unprocessed</div>
        )}
      </Card>
    );

    return linkTo ? (
      <Link to={linkTo} key={cardTitle} className="w-full">
        {cardBody}
      </Link>
    ) : (
      <div key={cardTitle} className="w-full">
        {cardBody}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 w-full">
        {topCards.map(renderTopCard)}
      </div>

      {(sourceCards.length > 0 || reportCards.length > 0) && (
        <div
          className={`grid gap-4 w-full ${
            sourceCards.length > 0 && reportCards.length > 0
              ? "grid-cols-1 lg:grid-cols-2"
              : "grid-cols-1"
          }`}
        >
          {sourceCards.length > 0 && (
            <Card className="w-full" style={customBoxShadow}>
              <CardHeader className="pb-2">
                <div className="flex items-start gap-2">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
                    <HiOutlineDocumentText size={16} />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-gray-900">
                      Document Sources
                    </CardTitle>
                    <CardDescription className="mt-0.5">
                      Where today's incoming documents came from
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {countLoading ? (
                  <Skeleton className="h-[180px] w-full" />
                ) : (
                  <div className="flex items-center gap-6">
                    <div className="relative h-[160px] w-[160px] shrink-0">
                      <div className="absolute inset-0 m-auto h-[120px] w-[120px] rounded-full bg-slate-50" />
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={50}
                            outerRadius={72}
                            paddingAngle={2}
                            stroke="none"
                          >
                            {pieData.map((entry) => (
                              <Cell
                                key={entry.name}
                                fill={getSourceColor(entry.name)}
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-xl font-bold text-gray-900">
                          {sourceTotal}
                        </span>
                        <span className="text-[10px] font-medium text-gray-400 tracking-wide">
                          TOTAL
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 min-w-0">
                      {sourceCards.map((card) => {
                        const sourceColor = getSourceColor(card.cardTitle);
                        const row = (
                          <div
                            className="grid grid-cols-[2rem_1fr_1px_2.75rem] items-center gap-2 rounded-lg px-3 py-2"
                            style={{ backgroundColor: `${sourceColor}12` }}
                          >
                            <span
                              className="flex h-8 w-8 items-center justify-center rounded-md"
                              style={{
                                backgroundColor: `${sourceColor}26`,
                                color: sourceColor,
                              }}
                            >
                              {card.icon}
                            </span>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-800 truncate">
                                {card.cardTitle}
                              </div>
                              <div className="text-[11px] text-gray-400 truncate">
                                {getSourceDescription(card.cardTitle)}
                              </div>
                            </div>
                            <div className="h-6 w-px justify-self-center bg-gray-300" />
                            <span
                              className="text-right text-sm font-bold"
                              style={{ color: sourceColor }}
                            >
                              {card.docCount}
                            </span>
                          </div>
                        );
                        return card.linkTo ? (
                          <Link
                            to={card.linkTo}
                            key={card.cardTitle}
                            className="hover:opacity-90"
                          >
                            {row}
                          </Link>
                        ) : (
                          <div key={card.cardTitle} className="opacity-50">
                            {row}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {reportCards.length > 0 && (
            <> 
              {/*
                Temporarily commenting out the Reports card.
                The original Reports UI is preserved below for quick rollback.

                <Card className="w-full" style={customBoxShadow}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">
                      Reports
                    </CardTitle>
                    <CardDescription>Quick access for admin review</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    {reportCards.map((card) => {
                      const row = (
                        <div
                          className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
                            card.linkTo
                              ? "hover:bg-teal-50 cursor-pointer"
                              : "bg-gray-100 cursor-not-allowed opacity-60"
                          }`}
                        >
                          <div className="p-2 rounded-lg bg-teal-50 text-[#1a6b7c]">
                            {card.icon}
                          </div>
                          <span className="flex-1 text-sm font-medium">
                            {card.cardTitle}
                          </span>
                          {card.linkTo && (
                            <span className="flex items-center gap-1 text-xs text-[#1a6b7c]">
                              View <HiOutlineChevronRight />
                            </span>
                          )}
                        </div>
                      );
                      return card.linkTo ? (
                        <Link to={card.linkTo} key={card.cardTitle}>
                          {row}
                        </Link>
                      ) : (
                        <div key={card.cardTitle}>{row}</div>
                      );
                    })}
                  </CardContent>
                </Card>
              */}

              {/* Show Pending Requests in place of Reports (temporary) */}
              <PendingRequestsCard
                customBoxShadow={customBoxShadow}
                token={token}
                is_admin={is_admin}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DocCountCard;
